import { formatter } from "../../shared/ts/formatter";
import Constants from "./constants";
import { StatePension } from "./statePension";
import store from "./store";
import { round, singularOrPlural } from "./utilities";

type IncomeClassification = "Flat" | "Stepped" | "Bespoke";

type StatePensionDescriptionInfo = { description: string, amount: number };

class RetireeDescriber {
  public incomeStatement(): string {
    if (store.state.retiree == null) {
      return "";
    }

    const income = store.state.retiree.requirements.income;
    const incomeClassification = this.classifyIncome(income);

    const solution = store.state.retiree.solution;
    const hasReducedIncomeSolution = solution.incomeAdjustor < 1;
    const solutionReducedIncomePercent = Math.round((1 - solution.incomeAdjustor) * 100);

    switch (incomeClassification) {
      case "Flat": {
        return this.getFlatIncomeStatement(income[0], hasReducedIncomeSolution, solutionReducedIncomePercent);
      }
      case "Stepped": {
        return this.getSteppedIncomeStatement(income, hasReducedIncomeSolution, solutionReducedIncomePercent);
      }
      case "Bespoke": {
        return this.getBespokeIncomeStatement(income[0], hasReducedIncomeSolution, solutionReducedIncomePercent);
      }
    }
  }

  public inflationStatement(): string {
    if (store.state.retiree == null) {
      return "";
    }

    if (!store.state.retiree.requirements.incomeIncreasesWithInflation) {
      return "You don't want automatic increases to your income.";
    }

    let inflationDuration = 'for life';
    if (store.state.retiree.requirements.incomeIncreasesWithInflationDuration != Constants.InflationDurationForLife) {
      inflationDuration = 'until the age of 75';
    }

    return `You want your income to increase by 2.5% a year ${inflationDuration}.`
  }

  public statePensionDescription(): StatePensionDescriptionInfo {
    if (store.state.retiree == null) {
      return { description: "", amount: 0 };
    }

    const statePension: StatePension = store.state.retiree.statePension;

    if (statePension.entitlementDate == null) {
      return { description: "", amount: 0 };
    }

    const entitlementDate = formatter.monthAndYearDate(
      new Date(statePension.entitlementDate)
    );
    const estimatedAmount = statePension.estimatedAmount != null
      ? formatter.formatCurrency(statePension.estimatedAmount)
      : "";

    if (statePension.isOverSPA) {
      if (!(statePension.actualAmount == null || statePension.actualAmount == 0)) {
        const actualAmount = formatter.formatCurrency(
          statePension.actualAmount
        );
        return { description: `Your State Pension is currently ${actualAmount} per annum.`, amount: statePension.actualAmount };
      } else {
        return { description: `The current full flat State Pension is ${estimatedAmount} per year. Many people get more or less than this. If you are, click edit to change this figure.`, amount: statePension.estimatedAmount };
      }
    } else {
      return { description: `You will be eligible to start claiming your State Pension from ${entitlementDate}. Your estimated pension from this date is ${estimatedAmount} per annum.`, amount: statePension.estimatedAmount };
    }
  }

  public getIncomeChartDescription(): string {
    if (!store.state.incomeChartData || store.state.incomeChartData.length == 0) {
      return "Awaiting plan data.";
    }

    const shortfallData = store.state.incomeChartData.find(ics => ics.name == Constants.ChartDataShortfallSeriesName);
    const deficitYears = shortfallData.data.filter(d => d > 0);
    const hasNoDeficit = deficitYears.length == 0;

    if (hasNoDeficit) {
      return "You are able to meet your target income in every year of your plan.";
    }

    const totalDeficit = deficitYears.reduce((sum, deficitInYear) => sum += deficitInYear);
    const totalDeficitFormatted = formatter.formatCurrency(totalDeficit);
    const years = singularOrPlural("year", deficitYears.length);
    const firstDeficitYear = shortfallData.data.indexOf(deficitYears[0]) + 1;
    return `You have a shortfall in ${deficitYears.length} ${years} of your income plan. The total shortfall is ${totalDeficitFormatted}. The first`
      + ` year with a shortfall is year ${firstDeficitYear} of your plan.`;
  }

  public getPotsChartDescription(): string {
    if (!store.state.potsChartData || store.state.potsChartData.length == 0) {
      return "Awaiting plan data.";
    }

    const savingsData = store.state.potsChartData.find(pcd => pcd.name == Constants.PotsChartDataSavingsSeriesName).data;
    const pensionPotsData = store.state.potsChartData.find(pcd => pcd.name == Constants.PotsChartDataPensionPotsSeriesName).data;
    const totalAssets = savingsData.map((s: number, i: number) => s + pensionPotsData[i]);

    const yearsWithoutAssets = totalAssets.filter(ta => ta == 0);
    const wontRunOutOfMoney = yearsWithoutAssets.length == 0;

    if (wontRunOutOfMoney) {
      const assetsAtEndOfPlan = totalAssets[totalAssets.length - 1];
      return `Your pension and savings pots are projected to hold ${formatter.formatCurrency(assetsAtEndOfPlan)} at the end of your retirement plan.`;
    }

    const firstYearWithoutPotAssets = totalAssets.indexOf(0) + 1;
    const numberOfYearsWithoutAssets = yearsWithoutAssets.length;
    const year = singularOrPlural("year", numberOfYearsWithoutAssets);
    return `Your pots were consumed in year ${firstYearWithoutPotAssets} of your retirement plan. You had a total of ${numberOfYearsWithoutAssets} ${year} in your plan that may have been affected by no funds in your pension pots and savings.`;
  }

  private classifyIncome(incomeArray: number[]): IncomeClassification {
    const nonZeroValues = incomeArray.filter(val => val !== 0);
    let classification: IncomeClassification = "Bespoke";

    if (nonZeroValues.every(val => val === nonZeroValues[0])) {
      classification = "Flat";
    } else {
      const firstChangeIndex = nonZeroValues.findIndex((val, index, arr) => index > 0 && val !== arr[index - 1]);
      const remainingValuesAreSame = nonZeroValues.slice(firstChangeIndex + 1).every(val => val === nonZeroValues[firstChangeIndex]);
      if (remainingValuesAreSame) {
        const steppedIncomeDetail = this.getSteppedIncomeDetail(incomeArray);
        if (steppedIncomeDetail.extraIncome > 0) {
          classification = "Stepped";
        }
      }
    }

    return classification;
  }

  private getSteppedIncomeDetail(incomeArray: number[]): { baseIncome: number, extraIncome: number, extraIncomeYears: number } {
    const incomeWithExtra = incomeArray[0];
    let extraIncomeYears = 0;
    let incomeWithoutExtra = 0;

    for (let i = 0; i < incomeArray.length; i++) {
      if (incomeArray[i] === 0 || incomeArray[i] !== incomeWithExtra) {
        incomeWithoutExtra = incomeArray[i]
        break;
      }
      extraIncomeYears++;
    }

    const extraIncome = incomeWithExtra - incomeWithoutExtra;
    const baseIncome = incomeWithoutExtra;
    return { baseIncome, extraIncome, extraIncomeYears };
  }

  private getFlatIncomeStatement(firstYearIncome: number, hasReducedIncomeSolution: boolean, solutionReducedIncomePercent: number): string {
    const baseStatement = `You want an after tax starting income of <b>${formatter.formatCurrency(firstYearIncome)}</b>.`;

    if (!hasReducedIncomeSolution) {
      return baseStatement;
    }

    return baseStatement.replace('want', 'wanted') + this.getSolutionStatement(solutionReducedIncomePercent);
  }

  private getSteppedIncomeStatement(income: number[], hasReducedIncomeSolution: boolean, solutionReducedIncomePercent: number): string {
    const steppedIncomeDetail = this.getSteppedIncomeDetail(income);
    const baseIncome = formatter.formatCurrency(steppedIncomeDetail.baseIncome);
    const extraIncome = formatter.formatCurrency(steppedIncomeDetail.extraIncome);
    const years = steppedIncomeDetail.extraIncomeYears == 1 ? "year" : `${steppedIncomeDetail.extraIncomeYears} years`;
    const baseStatement = `You want an after tax starting income of <b>${baseIncome}</b> plus an additional <b>${extraIncome}</b> for the first ${years}.`;

    if (!hasReducedIncomeSolution) {
      return baseStatement;
    }

    return baseStatement.replace('want', 'wanted') + this.getSolutionStatement(solutionReducedIncomePercent);
  }

  private getBespokeIncomeStatement(firstYearIncome: number, hasReducedIncomeSolution: boolean, solutionReducedIncomePercent: number): string {
    let baseStatement = `You want a bespoke income plan which has an after tax starting income of <b>${formatter.formatCurrency(firstYearIncome)}</b>.`;

    if (!hasReducedIncomeSolution) {
      return baseStatement;
    }

    baseStatement = baseStatement.replace('want', 'wanted').replace('has', 'had');

    return baseStatement + this.getSolutionStatement(solutionReducedIncomePercent);
  }

  private getSolutionStatement(solutionReducedIncomePercent: number): string {
    let reducedIncome = '';
    const solutionIncomeStepSize = 10;

    if (this.canSourceFirstYearIncome()) {
      const firstYearIncome = this.getFirstYearIncome();
      const reducedAmount = formatter.formatCurrency(round(firstYearIncome, solutionIncomeStepSize));
      reducedIncome = ` to <b>${reducedAmount}</b>`;
    }

    return ` To make your plan work you have reduced this by <b>${solutionReducedIncomePercent}%</b>${reducedIncome}.`;
  }

  private canSourceFirstYearIncome(): boolean {
    if (store.state.incomeChartData && store.state.incomeChartData.length > 0) {
      return true;
    }

    if (store.state.plan != null) {
      return true;
    }

    return false;
  }

  private getFirstYearIncome(): number {
    if (store.state.incomeChartData && store.state.incomeChartData.length > 0) {
      const incomeData = store.state.incomeChartData.find(ics => ics.name == Constants.ChartDataWhatYouWantSeriesName);
      return incomeData!.data[0];
    }

    if (store.state.plan != null) {
      return store.state.plan.years[0].totalAfterTax;
    }

    return -1;
  }
}

const retireeDescriber = new RetireeDescriber();

export { StatePensionDescriptionInfo, retireeDescriber };
